import { type ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { AppFooter } from './AppFooter';
import { buildNavItems, findActiveNavItem } from './navItems';
import { useAuth } from '@auth/AuthContext';
import { GlobalSettingsDrawer } from '@components/GlobalSettingsDrawer';

interface AppShellProps {
  children: ReactNode;
  productName?: string;
  companyName?: string;
}

export function AppShell({
  children,
  productName = 'Smart NVR',
  companyName,
}: AppShellProps): JSX.Element {
  const location = useLocation();
  const auth = useAuth();
  const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);
  const canManageRetention = auth.hasPermission('recording_control');
  const navItems = buildNavItems({
    canManageUsers: auth.hasPermission('user_management'),
    canManageRoles: auth.me?.roleSlug === 'super_admin',
  });
  const active = findActiveNavItem(location.pathname, navItems);
  const sectionTitle = active?.label ?? 'Smart NVR';

  const isAuthPage =
    location.pathname === '/login' || location.pathname === '/bootstrap';

  if (isAuthPage) {
    return (
      <div className="h-full min-h-0 w-full" style={{ backgroundColor: 'var(--app-bg)' }}>
        {children}
      </div>
    );
  }

  return (
    <div
      className="grid h-full min-h-0 w-full"
      style={{
        gridTemplateRows: 'var(--app-header-h) 1fr var(--app-footer-h)',
        gridTemplateColumns: 'auto 1fr',
        gridTemplateAreas: `
          "sidebar header"
          "sidebar main"
          "footer footer"
        `,
        backgroundColor: 'var(--app-bg)',
        color: 'var(--app-text-primary)',
      }}
    >
      <div style={{ gridArea: 'sidebar' }} className="h-full min-h-0">
        <AppSidebar productInitials="NVR" />
      </div>

      <div style={{ gridArea: 'header' }}>
        <AppHeader
          sectionTitle={sectionTitle}
          productName={productName}
          companyName={companyName}
          onSettingsClick={
            canManageRetention ? () => setGlobalSettingsOpen(true) : undefined
          }
        />
      </div>

      <GlobalSettingsDrawer
        open={globalSettingsOpen && canManageRetention}
        onClose={() => setGlobalSettingsOpen(false)}
      />

      <main
        style={{ gridArea: 'main' }}
        className="min-h-0 overflow-auto"
        id="app-main"
      >
        {children}
      </main>

      <div style={{ gridArea: 'footer' }}>
        <AppFooter />
      </div>
    </div>
  );
}
